const mockHttpClient = {
  get: jest.fn(),
  post: jest.fn(),
  defaults: {},
};

jest.mock('axios', () => ({
  create: jest.fn(() => mockHttpClient),
}));

jest.mock('@/store', () => ({
  state: {
    app: {
      announcements: [],
    },
  },
}));

jest.mock('@/services/apiService', () => ({
  tenantUpdateSystemAnnouncementState: jest.fn(),
}));

const validVerifyResponse = (overrides = {}) => ({
  data: {
    user: { id: 7, properties: { tenantIdThemeSetting: 1 } },
    isAuthenticated: true,
    featureFlags: { flags: [] },
    tenantId: 11,
    access: [],
    primaryScopeIds: [],
    primaryTenantIds: [],
    ...overrides,
  },
});

const verifyCalls = () => mockHttpClient.get.mock.calls.filter(([url]) => url === '/auth/verify');

describe('authentication actions verify', () => {
  let actions;
  let setTimeoutSpy;

  beforeEach(() => {
    jest.clearAllMocks();
    jest.resetModules();
    // Run backoff waits immediately so retry tests do not sleep for real.
    setTimeoutSpy = jest.spyOn(window, 'setTimeout').mockImplementation((fn) => {
      fn();
      return 0;
    });
    // Re-require after module reset so action module picks fresh mocks
    actions = require('../actions').default;
  });

  afterEach(() => {
    setTimeoutSpy.mockRestore();
  });

  it('sends X-API-Key when api key is provided', async () => {
    const commit = jest.fn();
    const apiKey = 'public-api-key';

    mockHttpClient.get
      .mockResolvedValueOnce({
        data: {
          user: { id: 7, properties: { tenantIdThemeSetting: 1 } },
          isAuthenticated: true,
          featureFlags: { flags: [] },
          tenantId: 11,
          access: [],
          primaryScopeIds: [],
          primaryTenantIds: [],
        },
      })
      .mockResolvedValueOnce({ data: [] });

    await actions.verify({ commit }, apiKey);

    expect(mockHttpClient.get).toHaveBeenNthCalledWith(1, '/auth/verify', {
      params: { domain: window.location.hostname },
      headers: { 'X-API-Key': apiKey },
    });
    expect(mockHttpClient.get).toHaveBeenNthCalledWith(2, '/user/own/announcements', {
      headers: { 'X-API-Key': apiKey },
    });
    expect(commit).toHaveBeenCalledWith('app/setFeatureFlags', [], { root: true });
    expect(commit).toHaveBeenCalledWith(
      'auth_success',
      expect.objectContaining({
        data: expect.objectContaining({ isApiKey: true }),
      })
    );
  });

  it('commits auth_success even when announcements request fails', async () => {
    const commit = jest.fn();

    mockHttpClient.get
      .mockResolvedValueOnce({
        data: {
          user: { id: 7, properties: { tenantIdThemeSetting: 1 } },
          isAuthenticated: true,
          featureFlags: { flags: [] },
          tenantId: 11,
          access: [],
          primaryScopeIds: [],
          primaryTenantIds: [],
        },
      })
      .mockRejectedValueOnce(new Error('Announcements request failed'));

    await actions.verify({ commit }, 'public-api-key');

    expect(commit).toHaveBeenCalledWith(
      'auth_success',
      expect.objectContaining({
        data: expect.objectContaining({
          user: expect.objectContaining({ id: 7 }),
          tenantId: 11,
        }),
      })
    );
  });

  it('does not send X-API-Key when no api key is provided', async () => {
    const commit = jest.fn();

    mockHttpClient.get
      .mockResolvedValueOnce({
        data: {
          user: { id: 9, properties: { tenantIdThemeSetting: 1 } },
          isAuthenticated: true,
          featureFlags: { flags: ['a'] },
          tenantId: 15,
          access: [],
          primaryScopeIds: [],
          primaryTenantIds: [],
        },
      })
      .mockResolvedValueOnce({ data: [] });

    await actions.verify({ commit });

    expect(mockHttpClient.get).toHaveBeenNthCalledWith(1, '/auth/verify', {
      params: { domain: window.location.hostname },
    });
    expect(mockHttpClient.get).toHaveBeenNthCalledWith(2, '/user/own/announcements', undefined);
    expect(commit).toHaveBeenCalledWith(
      'auth_success',
      expect.objectContaining({
        data: expect.objectContaining({ isApiKey: false }),
      })
    );
  });

  it('commits logout when verify request fails with auth error', async () => {
    const commit = jest.fn();
    const err = new Error('Unauthorized');
    err.isAuthError = true;

    mockHttpClient.get.mockRejectedValueOnce(err);

    await expect(actions.verify({ commit }, 'bad-key')).rejects.toThrow('Unauthorized');
    expect(commit).toHaveBeenCalledWith('logout');
  });

  it('commits logout when verify returns an invalid session', async () => {
    const commit = jest.fn();

    mockHttpClient.get.mockResolvedValueOnce({
      data: {
        user: null,
        isAuthenticated: false,
      },
    });

    await expect(actions.verify({ commit }, 'bad-key')).rejects.toThrow('Auth error');
    expect(commit).toHaveBeenCalledWith('logout');
  });

  it('commits logout and tags the error when verify responds 401', async () => {
    const commit = jest.fn();
    const err = new Error('Unauthorized');
    err.response = { status: 401 };

    mockHttpClient.get.mockRejectedValueOnce(err);

    await expect(actions.verify({ commit }, 'bad-key')).rejects.toMatchObject({ isAuthError: true });
    expect(commit).toHaveBeenCalledWith('logout');
  });

  it('does not commit logout or tag the error when verify fails with a server error', async () => {
    const commit = jest.fn();
    const err = new Error('Internal server error');
    err.response = { status: 500 };

    mockHttpClient.get.mockRejectedValue(err);

    await expect(actions.verify({ commit }, 'bad-key')).rejects.toThrow('Internal server error');
    expect(commit).not.toHaveBeenCalledWith('logout');
    expect(err.isAuthError).toBeUndefined();
  });

  it('commits verify_failed instead of logout when verify fails transiently', async () => {
    const commit = jest.fn();
    const err = new Error('Network timeout');

    mockHttpClient.get.mockRejectedValue(err);

    await expect(actions.verify({ commit }, 'bad-key')).rejects.toThrow('Network timeout');
    expect(commit).toHaveBeenCalledWith('verify_failed');
    expect(commit).not.toHaveBeenCalledWith('logout');
  });

  it('does not commit verify_failed when the failure is an auth error', async () => {
    const commit = jest.fn();
    const err = new Error('Unauthorized');
    err.response = { status: 401 };

    mockHttpClient.get.mockRejectedValueOnce(err);

    await expect(actions.verify({ commit }, 'bad-key')).rejects.toThrow('Unauthorized');
    expect(commit).toHaveBeenCalledWith('logout');
    expect(commit).not.toHaveBeenCalledWith('verify_failed');
  });

  it('does not commit logout when suppressLogoutOnError is enabled', async () => {
    const commit = jest.fn();
    const err = new Error('Network timeout');

    mockHttpClient.get.mockRejectedValue(err);

    await expect(actions.verify({ commit }, { suppressLogoutOnError: true })).rejects.toThrow('Network timeout');
    expect(commit).not.toHaveBeenCalledWith('logout');
    expect(commit).not.toHaveBeenCalledWith('verify_failed');
  });

  it('retries a transient verify failure and resolves once it succeeds', async () => {
    const commit = jest.fn();
    const err = new Error('Internal server error');
    err.response = { status: 500 };

    mockHttpClient.get
      .mockRejectedValueOnce(err)
      .mockResolvedValueOnce(validVerifyResponse())
      .mockResolvedValueOnce({ data: [] });

    await actions.verify({ commit });

    expect(verifyCalls()).toHaveLength(2);
    expect(commit).toHaveBeenCalledWith('auth_success', expect.objectContaining({ data: expect.any(Object) }));
    expect(commit).not.toHaveBeenCalledWith('verify_failed');
  });

  it('gives up after three retries and commits verify_failed', async () => {
    const commit = jest.fn();
    const err = new Error('Network timeout');

    mockHttpClient.get.mockRejectedValue(err);

    await expect(actions.verify({ commit })).rejects.toThrow('Network timeout');

    expect(verifyCalls()).toHaveLength(4);
    expect(commit).toHaveBeenCalledWith('verify_failed');
  });

  it('backs off roughly 1s, 2s and 4s with jitter between retries', async () => {
    const commit = jest.fn();
    mockHttpClient.get.mockRejectedValue(new Error('Network timeout'));

    await expect(actions.verify({ commit })).rejects.toThrow('Network timeout');

    const delays = setTimeoutSpy.mock.calls.map(([, delay]) => delay);
    expect(delays).toHaveLength(3);
    [1000, 2000, 4000].forEach((base, index) => {
      expect(delays[index]).toBeGreaterThanOrEqual(base * 0.75);
      expect(delays[index]).toBeLessThanOrEqual(base * 1.25);
    });
  });

  it('does not retry auth errors', async () => {
    const commit = jest.fn();
    const err = new Error('Unauthorized');
    err.response = { status: 401 };

    mockHttpClient.get.mockRejectedValue(err);

    await expect(actions.verify({ commit })).rejects.toThrow('Unauthorized');

    expect(verifyCalls()).toHaveLength(1);
    expect(setTimeoutSpy).not.toHaveBeenCalled();
    expect(commit).toHaveBeenCalledWith('logout');
  });

  it('shares a single in-flight request across concurrent verify dispatches', async () => {
    const commit = jest.fn();
    let resolveVerify;

    mockHttpClient.get
      .mockImplementationOnce(
        () =>
          new Promise((resolve) => {
            resolveVerify = resolve;
          })
      )
      .mockResolvedValueOnce({ data: [] });

    const pending = [actions.verify({ commit }), actions.verify({ commit }), actions.verify({ commit })];
    resolveVerify(validVerifyResponse());
    await Promise.all(pending);

    expect(verifyCalls()).toHaveLength(1);
    expect(mockHttpClient.get.mock.calls.filter(([url]) => url === '/user/own/announcements')).toHaveLength(1);
  });

  it('rejects every concurrent caller from the shared request without extra retry chains', async () => {
    const commit = jest.fn();
    mockHttpClient.get.mockRejectedValue(new Error('Network timeout'));

    const results = await Promise.allSettled([actions.verify({ commit }), actions.verify({ commit })]);

    expect(results.map((result) => result.status)).toEqual(['rejected', 'rejected']);
    expect(verifyCalls()).toHaveLength(4);
  });

  it('starts a fresh request once the previous one has settled', async () => {
    const commit = jest.fn();

    mockHttpClient.get
      .mockResolvedValueOnce(validVerifyResponse())
      .mockResolvedValueOnce({ data: [] })
      .mockResolvedValueOnce(validVerifyResponse())
      .mockResolvedValueOnce({ data: [] });

    await actions.verify({ commit });
    await actions.verify({ commit });

    expect(verifyCalls()).toHaveLength(2);
  });
});

describe('authentication actions login', () => {
  let actions;

  beforeEach(() => {
    jest.clearAllMocks();
    jest.resetModules();
    actions = require('../actions').default;
  });

  it('commits auth_success from login response even if verify fails', async () => {
    const commit = jest.fn();
    const dispatch = jest.fn().mockRejectedValue(new Error('verify timeout'));

    const loginResponse = {
      data: {
        user: { id: 7, properties: { tenantIdThemeSetting: 1 } },
        isAuthenticated: true,
        twoFactorSetup: {
          required: false,
          state: 'completed',
          completed: true,
          setupRequired: false,
        },
        access: [],
        primaryScopeIds: [],
        primaryTenantIds: [],
        tenantId: 11,
      },
    };

    mockHttpClient.post.mockResolvedValueOnce(loginResponse);

    const response = await actions.login.call(
      { state: { app: {} } },
      { commit, dispatch },
      { username: 'user', password: 'pw' }
    );

    expect(response).toBe(loginResponse);
    expect(commit).toHaveBeenNthCalledWith(1, 'auth_request');
    expect(commit).toHaveBeenNthCalledWith(
      2,
      'auth_success',
      expect.objectContaining({
        data: expect.objectContaining({
          user: expect.objectContaining({ id: 7 }),
          isApiKey: false,
        }),
      })
    );
    expect(dispatch).toHaveBeenCalledWith('verify', { suppressLogoutOnError: true });
    expect(commit).not.toHaveBeenCalledWith('logout');
  });

  it('commits logout when login request fails', async () => {
    const commit = jest.fn();
    const dispatch = jest.fn();
    const loginError = { response: { status: 401, data: { error: 'AUTH_LOGIN_PASSWORD_MISMATCH' } } };

    mockHttpClient.post.mockRejectedValueOnce(loginError);

    await expect(
      actions.login.call({ state: { app: {} } }, { commit, dispatch }, { username: 'user', password: 'wrong' })
    ).rejects.toEqual(loginError.response);
    expect(dispatch).not.toHaveBeenCalled();
    expect(commit).toHaveBeenCalledWith('logout');
  });
});
